/** Геометрия швов напольного покрытия, общая для 2D SVG и 3D CanvasTexture. */
import { FloorLayout, FloorMaterial, type FloorLook } from "./types";

export interface FloorPatternBounds {
	minX: number;
	minY: number;
	maxX: number;
	maxY: number;
}

export interface FloorPatternSegment {
	fromX: number;
	fromY: number;
	toX: number;
	toY: number;
}

const MIN_SIZE = 20;

const segment = (
	segments: FloorPatternSegment[],
	fromX: number,
	fromY: number,
	toX: number,
	toY: number,
) => segments.push({ fromX, fromY, toX, toY });

const rectangle = (
	segments: FloorPatternSegment[],
	x: number,
	y: number,
	width: number,
	height: number,
) => {
	segment(segments, x, y, x + width, y);
	segment(segments, x + width, y, x + width, y + height);
	segment(segments, x + width, y + height, x, y + height);
	segment(segments, x, y + height, x, y);
};

/** Размеры планки так же, как их берут швы: целые миллиметры и не меньше `MIN_SIZE`. */
const plankSize = (look: FloorLook) => ({
	width: Math.max(MIN_SIZE, Math.round(look.width)),
	length: Math.max(MIN_SIZE, Math.round(look.length)),
});

/**
 * Период узора в мм — через сколько он в точности повторяется. Его использует 3D-текстура:
 * картинка ровно в период ложится на пол без стыков. Узор, начатый в точке, сдвинутой
 * на целое число периодов, совпадает с исходным.
 * Ёлки: французская — столбцы планок через один зеркальны, столбец повторяется через
 * высоту одной планки по вертикали; английская — квадрат в две длины ёлки.
 */
export const floorPatternCycle = (look: FloorLook) => {
	const { width, length } = plankSize(look);
	switch (look.layout) {
		case FloorLayout.Tile:
			return look.material === FloorMaterial.Tile
				? { width, height: length }
				: { width: length, height: width };
		case FloorLayout.Straight:
		case FloorLayout.Half:
		case FloorLayout.Third:
			return { width: length, height: width * 2 };
		case FloorLayout.FrenchHerringbone: {
			const angle = Math.PI / 4;
			return { width: length * Math.cos(angle) * 2, height: width / Math.sin(angle) };
		}
		case FloorLayout.EnglishHerringbone: {
			const side = 2 * Math.round(length / width) * width;
			return { width: side, height: side };
		}
	}
};

/**
 * Одна планка или плитка узора: многоугольник в мм и направление её длинной стороны,
 * радианы. По нему 3D кладёт рисунок дерева вдоль доски, а у ёлки — под углом.
 */
export interface FloorPatternCell {
	points: { x: number; y: number }[];
	angle: number;
}

const rect = (
	x: number,
	y: number,
	width: number,
	height: number,
	angle = 0,
): FloorPatternCell => ({
	points: [
		{ x, y },
		{ x: x + width, y },
		{ x: x + width, y: y + height },
		{ x, y: y + height },
	],
	angle,
});

/**
 * Планки и плитки узора в области `region` (мм). Узор начинается ровно в её левом верхнем
 * углу — теми же шагами, что и швы `floorPatternSegments` с нулевым запасом, поэтому
 * планки совпадают со швами. Планки, вылезающие за край области, тоже попадают в список.
 */
export const floorPatternCells = (
	region: FloorPatternBounds,
	look: FloorLook,
): FloorPatternCell[] => {
	const cells: FloorPatternCell[] = [];
	const { minX, minY, maxX, maxY } = region;
	const { width, length } = plankSize(look);

	switch (look.layout) {
		case FloorLayout.Tile: {
			const stepX = look.material === FloorMaterial.Tile ? width : length;
			const stepY = look.material === FloorMaterial.Tile ? length : width;
			for (let y = minY; y < maxY; y += stepY) {
				for (let x = minX; x < maxX; x += stepX) cells.push(rect(x, y, stepX, stepY));
			}
			break;
		}
		case FloorLayout.Straight:
		case FloorLayout.Half:
		case FloorLayout.Third: {
			const offset =
				look.layout === FloorLayout.Straight
					? length / 2
					: look.layout === FloorLayout.Half
						? length / 3
						: length / 4;
			for (let row = 0; minY + row * width < maxY; row++) {
				const y = minY + row * width;
				// Нечётный ряд сдвинут: его первая доска начинается левее области
				const start = row % 2 === 0 ? minX : minX + offset - length;
				for (let x = start; x < maxX; x += length) cells.push(rect(x, y, length, width));
			}
			break;
		}
		case FloorLayout.FrenchHerringbone: {
			const angle = Math.PI / 4;
			const stepX = length * Math.cos(angle);
			const stepY = width / Math.sin(angle);
			const shiftY = length * Math.sin(angle);
			const columns = Math.ceil((maxX - minX) / stepX) + 2;
			const rows = Math.ceil((maxY - minY) / stepY) + 2;
			for (let column = -1; column < columns; column++) {
				const flipped = column % 2 !== 0;
				const x = minX + column * stepX;
				for (let row = -2; row < rows; row++) {
					const y = minY + row * stepY;
					cells.push(
						flipped
							? {
									points: [
										{ x, y: y + shiftY },
										{ x: x + stepX, y },
										{ x: x + stepX, y: y + stepY },
										{ x, y: y + shiftY + stepY },
									],
									angle: -angle,
								}
							: {
									points: [
										{ x, y },
										{ x: x + stepX, y: y + shiftY },
										{ x: x + stepX, y: y + shiftY + stepY },
										{ x, y: y + stepY },
									],
									angle,
								},
					);
				}
			}
			break;
		}
		case FloorLayout.EnglishHerringbone: {
			const ratio = Math.round(length / width);
			const period = 2 * ratio;
			const extra = Math.ceil(Math.hypot(maxX - minX, maxY - minY) / width) + 5;
			const rows = Math.ceil((maxY - minY) / width);
			for (let row = -extra; row < rows + extra; row++) {
				const offset = ((-row % period) + period) % period;
				let column = offset + Math.ceil((-extra - offset) / period) * period;
				for (; column * width < maxX - minX; column += period) {
					const x = minX + column * width;
					const y = minY + row * width;
					if (y > maxY) continue;
					cells.push(rect(x, y, length, width));
					cells.push(rect(x + length, y, width, length, Math.PI / 2));
				}
			}
			break;
		}
	}
	return cells;
};

/** Отрезки швов укладки в миллиметрах, с запасом за границами комнаты для обрезки. */
export const floorPatternSegments = (
	bounds: FloorPatternBounds,
	look: FloorLook,
	padding = 2000,
): FloorPatternSegment[] => {
	const segments: FloorPatternSegment[] = [];
	const minX = bounds.minX - padding;
	const maxX = bounds.maxX + padding;
	const minY = bounds.minY - padding;
	const maxY = bounds.maxY + padding;
	const width = Math.max(MIN_SIZE, Math.round(look.width));
	const length = Math.max(MIN_SIZE, Math.round(look.length));

	if (look.layout === FloorLayout.Tile) {
		const stepX = look.material === FloorMaterial.Tile ? width : length;
		const stepY = look.material === FloorMaterial.Tile ? length : width;
		for (let x = minX; x <= maxX; x += stepX) segment(segments, x, minY, x, maxY);
		for (let y = minY; y <= maxY; y += stepY) segment(segments, minX, y, maxX, y);
		return segments;
	}

	switch (look.layout) {
		case FloorLayout.Straight:
		case FloorLayout.Half:
		case FloorLayout.Third: {
			const offset =
				look.layout === FloorLayout.Straight
					? length / 2
					: look.layout === FloorLayout.Half
						? length / 3
						: length / 4;
			for (let y = minY; y <= maxY; y += width) segment(segments, minX, y, maxX, y);
			for (let y = minY; y <= maxY; y += width * 2) {
				for (let x = minX; x <= maxX; x += length) segment(segments, x, y, x, y + width);
			}
			for (let y = minY + width; y <= maxY; y += width * 2) {
				for (let x = minX + offset; x <= maxX; x += length) {
					segment(segments, x, y, x, y + width);
				}
			}
			break;
		}
		case FloorLayout.FrenchHerringbone: {
			const angle = Math.PI / 4;
			const stepX = length * Math.cos(angle);
			const stepY = width / Math.sin(angle);
			const shiftY = length * Math.sin(angle);
			const columns = Math.ceil((maxX - minX) / stepX) + 2;
			const rows = Math.ceil((maxY - minY) / stepY) + 2;
			for (let column = -1; column < columns; column++) {
				const flipped = column % 2 !== 0;
				const x = minX + column * stepX;
				for (let row = -1; row < rows; row++) {
					const y = minY + row * stepY;
					const points = flipped
						? [
								[x, y + shiftY],
								[x + stepX, y],
								[x + stepX, y + stepY],
								[x, y + shiftY + stepY],
							]
						: [
								[x, y],
								[x + stepX, y + shiftY],
								[x + stepX, y + shiftY + stepY],
								[x, y + stepY],
							];
					for (let index = 0; index < points.length; index++) {
						const from = points[index];
						const to = points[(index + 1) % points.length];
						segment(segments, from[0], from[1], to[0], to[1]);
					}
				}
			}
			break;
		}
		case FloorLayout.EnglishHerringbone: {
			const ratio = Math.round(length / width);
			const period = 2 * ratio;
			const extra = Math.ceil(Math.hypot(maxX - minX, maxY - minY) / width) + 5;
			const rows = Math.ceil((maxY - minY) / width);
			for (let row = -extra; row < rows + extra; row++) {
				const offset = ((-row % period) + period) % period;
				let column = offset + Math.ceil((-extra - offset) / period) * period;
				for (; column * width < maxX - minX + padding * 2; column += period) {
					const x = minX + column * width;
					const y = minY + row * width;
					rectangle(segments, x, y, length, width);
					rectangle(segments, x + length, y, width, length);
				}
			}
			break;
		}
	}

	return segments;
};
